import React, { useState } from 'react';
import { Select } from '../../components/form/Select';

import { useTranslation } from 'react-i18next';
import { getBestContrastColor } from '../common/color.helper';
import { formatInteger } from '../common/formatHelper';
import {
  ABC_COLORS,
  ABC_XYZ_COLORS,
  getABCXYZCategory,
  getWeeksFromData,
  getXYZClassification,
  SKUData,
  XYZ_COLORS,
} from './sku-profiling';

interface SKUMatrixControlProps {
  selectedABC?: string | null;
  selectedXYZ?: string | null;
  selectedCategory?: string | null;
  onABCSelect?: (abc: string | null) => void;
  onXYZSelect?: (xyz: string | null) => void;
  onCategorySelect?: (category: string | null) => void;
  skuData: SKUData[];
  viewMode?: 'sku' | 'lines' | 'percent';
}

export const SKUMatrixControl: React.FC<SKUMatrixControlProps> = ({
  selectedABC,
  selectedXYZ,
  selectedCategory,
  onABCSelect,
  onXYZSelect,
  onCategorySelect,
  skuData,
}) => {
  const { t } = useTranslation('app');
  const [viewMode, setViewMode] = useState<'sku' | 'lines' | 'percent'>('sku');

  const getCount = (items: SKUData[]) => {
    if (viewMode === 'percent') {
      const totalCount = skuData.length;
      return totalCount > 0 ? (items.length / totalCount) * 100 : 0;
    }
    return viewMode === 'sku'
      ? items.length
      : items.reduce((sum, sku) => sum + sku.order_line_count, 0);
  };

  const getFilteredSkus = (abc?: string, xyz?: string, category?: string) => {
    return skuData.filter(sku => {
      const weeks = getWeeksFromData(sku);
      const skuXYZ = getXYZClassification(sku, weeks);
      const skuCategory = `${sku.rank}${skuXYZ}`;
      return (
        (!abc || sku.rank === abc) &&
        (!xyz || skuXYZ === xyz) &&
        (!category || skuCategory === category)
      );
    });
  };

  const handleClick = (type: 'abc' | 'xyz' | 'category', value: string) => {
    if (type === 'abc') {
      onABCSelect?.(selectedABC === value ? null : value);
      onXYZSelect?.(null);
      onCategorySelect?.(null);
    } else if (type === 'xyz') {
      onXYZSelect?.(selectedXYZ === value ? null : value);
      onABCSelect?.(null);
      onCategorySelect?.(null);
    } else {
      onCategorySelect?.(selectedCategory === value ? null : value);
      onABCSelect?.(null);
      onXYZSelect?.(null);
    }
  };

  const skuDistribution = skuData.map(sku => {
    const abc = sku.rank as string;
    const weeks = getWeeksFromData(sku);
    const xyz = getXYZClassification(sku, weeks);
    return {
      abc: sku.rank,
      xyz: getXYZClassification(sku, weeks),
    };
  });

  return (
    <div className="w-full max-w-[600px]">
      <table
        className="grid w-full border-collapse text-sm"
        style={{
          gridTemplateColumns: 'repeat(4, 1fr)',
          maxHeight: '380px',
        }}
      >
        <thead className="contents">
          <tr className="contents">
            <th className="p-px">
              <div className="flex h-full w-full flex-col items-center justify-center gap-1">
                <Select
                  value={viewMode}
                  onChange={e =>
                    setViewMode(e.target.value as 'sku' | 'lines' | 'percent')
                  }
                  className="w-full"
                >
                  <option value="sku">{t`SKU Count`}</option>
                  <option value="lines">{t`Lines Count`}</option>
                  <option value="percent">{t`Percentage`}</option>
                </Select>

                {
                  selectedABC || selectedXYZ || selectedCategory ? (
                    <button
                      className="bg-app-header-menu w-full rounded-full py-1"
                      onClick={() => {
                        onABCSelect?.(null);
                        onXYZSelect?.(null);
                        onCategorySelect?.(null);
                      }}
                    >{t`Clear`}</button>
                  ) : null
                  //t`Class`
                }
              </div>
            </th>
            {['X', 'Y', 'Z'].map(xyz => (
              <th
                key={xyz}
                onClick={() => handleClick('xyz', xyz)}
                className={`flex h-[60px] w-full cursor-pointer flex-col items-center justify-center gap-1 p-1 ${
                  selectedXYZ === xyz || selectedCategory?.includes(xyz)
                    ? 'bg-app-header-menu'
                    : ''
                }`}
              >
                <div
                  className="flex h-full w-full flex-col items-center justify-center rounded-sm"
                  style={{
                    backgroundColor: XYZ_COLORS[xyz],
                    color: getBestContrastColor(XYZ_COLORS[xyz]),
                    opacity:
                      (selectedXYZ && selectedXYZ !== xyz) ||
                      selectedABC ||
                      (selectedCategory && !selectedCategory.includes(xyz))
                        ? 0.3
                        : 1,
                  }}
                >
                  <span className="text-lg">{xyz}</span>
                  <span className="font-bold">
                    {(() => {
                      const count = getCount(getFilteredSkus(undefined, xyz));
                      return count > 0
                        ? `${formatInteger(count)}${viewMode === 'percent' ? '%' : ''}`
                        : '';
                    })()}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="contents">
          {['A+', 'A', 'B', 'C'].map(abc => (
            <tr key={abc} className="contents">
              <td
                onClick={() => handleClick('abc', abc)}
                className={`flex h-full w-full cursor-pointer flex-col items-center justify-center gap-1 p-0.5 ${
                  selectedABC === abc || selectedCategory?.includes(abc)
                    ? 'bg-app-header-menu'
                    : ''
                }`}
              >
                <div
                  className="flex h-full w-full flex-col items-center justify-center rounded-sm p-1"
                  style={{
                    backgroundColor: ABC_COLORS[abc],
                    color: getBestContrastColor(ABC_COLORS[abc]),
                    opacity:
                      (selectedABC && selectedABC !== abc) ||
                      selectedXYZ ||
                      (selectedCategory && !selectedCategory.includes(abc))
                        ? 0.3
                        : 1,
                  }}
                >
                  <span className="text-lg">{abc}</span>
                  <span className="text-base font-bold">
                    {(() => {
                      const count = getCount(getFilteredSkus(abc));
                      return count > 0
                        ? `${formatInteger(count)}${viewMode === 'percent' ? '%' : ''}`
                        : '';
                    })()}
                  </span>
                </div>
              </td>
              {['X', 'Y', 'Z'].map((xyz, xyzIndex) => {
                const category = getABCXYZCategory(t, abc, xyz);
                return (
                  <td
                    key={`${abc}-${xyz}`}
                    onClick={() => handleClick('category', category)}
                    className={`flex h-full w-full cursor-pointer flex-col items-center justify-center gap-1 p-px ${
                      selectedCategory === `${abc}${xyz}` ||
                      (selectedABC === abc && selectedXYZ === xyz)
                        ? 'bg-blue-50'
                        : ''
                    }`}
                  >
                    <div
                      className="flex h-full w-full flex-col items-center justify-center rounded-sm"
                      style={{
                        backgroundColor: ABC_XYZ_COLORS[category],
                        color: getBestContrastColor(ABC_XYZ_COLORS[category]),
                        opacity:
                          selectedCategory && selectedCategory !== category
                            ? 0.3
                            : 1,
                      }}
                    >
                      <span>{category}</span>
                      <span className="text-xs font-bold">
                        {(() => {
                          const count = getCount(getFilteredSkus(abc, xyz));
                          return count > 0
                            ? `${formatInteger(count)}${viewMode === 'percent' ? '%' : ''}`
                            : '';
                        })()}
                      </span>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
